import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox, CheckboxGroup } from '../../../components/ui/Checkbox';

const ContactPreferencesForm = ({ formData, updateFormData, errors }) => {
  const roleOptions = [
    { value: 'ceo', label: 'CEO / Presidente' },
    { value: 'cto', label: 'CTO / Director de Tecnología' },
    { value: 'cfo', label: 'CFO / Director Financiero' },
    { value: 'operations', label: 'Director de Operaciones' },
    { value: 'it_manager', label: 'Gerente de TI' },
    { value: 'project_manager', label: 'Gerente de Proyecto' },
    { value: 'business_analyst', label: 'Analista de Negocio' },
    { value: 'owner', label: 'Propietario' },
    { value: 'other', label: 'Otro' }
  ];

  const preferredContactOptions = [
    { value: 'email', label: 'Correo electrónico' },
    { value: 'phone', label: 'Llamada telefónica' },
    { value: 'video_call', label: 'Videollamada' },
    { value: 'in_person', label: 'Reunión presencial' }
  ];

  const timePreferenceOptions = [
    { value: 'morning', label: 'Mañana (9:00 - 12:00)' },
    { value: 'afternoon', label: 'Tarde (12:00 - 17:00)' },
    { value: 'evening', label: 'Noche (17:00 - 20:00)' },
    { value: 'flexible', label: 'Horario flexible' }
  ];

  const timezoneOptions = [
    { value: 'UTC-5', label: 'UTC-5 (Colombia, Perú, Ecuador)' },
    { value: 'UTC-6', label: 'UTC-6 (México, Guatemala)' },
    { value: 'UTC-3', label: 'UTC-3 (Argentina, Chile, Uruguay)' },
    { value: 'UTC-4', label: 'UTC-4 (Venezuela, Bolivia)' },
    { value: 'UTC-2', label: 'UTC-2 (Brasil)' },
    { value: 'other', label: 'Otra zona horaria' }
  ];

  const handleContactMethodChange = (value, checked) => {
    const methods = formData.preferredContactMethods || [];
    if (checked) {
      updateFormData('preferredContactMethods', [...methods, value]);
    } else {
      updateFormData('preferredContactMethods', methods.filter(item => item !== value));
    }
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Nombre Completo"
          type="text"
          placeholder="Ingrese su nombre completo"
          value={formData.contactName || ''}
          onChange={(e) => updateFormData('contactName', e.target.value)}
          error={errors.contactName}
          required
        />

        <Input
          label="Cargo / Posición"
          type="text"
          placeholder="Ingrese su cargo en la empresa"
          value={formData.jobTitle || ''}
          onChange={(e) => updateFormData('jobTitle', e.target.value)}
          error={errors.jobTitle}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Correo Electrónico"
          type="email"
          placeholder="correo@empresa.com"
          value={formData.email || ''}
          onChange={(e) => updateFormData('email', e.target.value)}
          error={errors.email}
          required
        />

        <Input
          label="Teléfono"
          type="tel"
          placeholder="+57 300 123 4567"
          value={formData.phone || ''}
          onChange={(e) => updateFormData('phone', e.target.value)}
          error={errors.phone}
          required
        />
      </div>

      <Select
        label="Rol en la Decisión"
        placeholder="¿Cuál es su rol en la toma de decisiones?"
        options={roleOptions}
        value={formData.decisionRole || ''}
        onChange={(value) => updateFormData('decisionRole', value)}
        error={errors.decisionRole}
        required
        description="Esto nos ayuda a preparar la consultoría adecuada"
      />

      <div>
        <h4 className="text-lg font-semibold text-foreground mb-4">Preferencias de Contacto</h4>
        <CheckboxGroup 
          label="¿Cómo prefiere que nos comuniquemos?"
          description="Seleccione todos los métodos que le resulten convenientes"
          error={errors.preferredContactMethods}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {preferredContactOptions.map((option) => (
              <Checkbox
                key={option.value}
                label={option.label}
                checked={(formData.preferredContactMethods || []).includes(option.value)}
                onChange={(e) => handleContactMethodChange(option.value, e.target.checked)}
              />
            ))}
          </div>
        </CheckboxGroup>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Horario Preferido"
          placeholder="¿Cuándo prefiere las reuniones?"
          options={timePreferenceOptions}
          value={formData.timePreference || ''}
          onChange={(value) => updateFormData('timePreference', value)}
          error={errors.timePreference}
        />

        <Select
          label="Zona Horaria"
          placeholder="Seleccione su zona horaria"
          options={timezoneOptions}
          value={formData.timezone || ''}
          onChange={(value) => updateFormData('timezone', value)}
          error={errors.timezone}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Fecha Preferida"
          type="date"
          value={formData.preferredDate || ''}
          onChange={(e) => updateFormData('preferredDate', e.target.value)}
          error={errors.preferredDate}
          description="Fecha ideal para la primera consultoría"
        />

        <Input
          label="Duración Estimada"
          type="text"
          placeholder="Ej: 1-2 horas"
          value={formData.estimatedDuration || ''}
          onChange={(e) => updateFormData('estimatedDuration', e.target.value)}
          error={errors.estimatedDuration}
          description="¿Cuánto tiempo puede dedicar a la consultoría?"
        />
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-foreground mb-3">Participantes Adicionales</h4>
        <Input
          label="Otros Participantes"
          type="text"
          placeholder="Nombres y cargos de otras personas que participarán en la consultoría"
          value={formData.additionalParticipants || ''}
          onChange={(e) => updateFormData('additionalParticipants', e.target.value)}
          error={errors.additionalParticipants}
          description="Ej: María González - Gerente Financiera, Carlos López - Jefe de Operaciones"
        />
      </div>

      <div className="flex items-start space-x-3">
        <Checkbox
          label="Acepto recibir comunicaciones de seguimiento sobre la consultoría y soluciones de Automatización"
          checked={formData.acceptCommunications || false}
          onChange={(e) => updateFormData('acceptCommunications', e.target.checked)}
          required
        />
      </div>

      <div className="flex items-start space-x-3">
        <Checkbox
          label="Acepto los términos y condiciones de la consultoría diagnóstica"
          checked={formData.acceptTerms || false}
          onChange={(e) => updateFormData('acceptTerms', e.target.checked)}
          required
        />
      </div>
    </div>
  );
};

export default ContactPreferencesForm;